import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, History, Download, Undo2, Loader2, FileText, Images, FileSpreadsheet, CheckCircle2, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_META = {
  rate_con: { label: 'Rate Confirmation', icon: FileText },
  load_list: { label: 'Load List', icon: Images },
  amazon_relay: { label: 'Amazon Relay', icon: Images },
  spreadsheet: { label: 'Spreadsheet', icon: FileSpreadsheet },
};

const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '—');

const ImportHistoryModal = ({ open, onClose, onUndone }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [undoing, setUndoing] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/loads/import-batches`, { withCredentials: true });
      setItems(res.data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load import history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) { load(); setConfirmId(null); } }, [open, load]);

  const download = async (b) => {
    try {
      const res = await axios.get(`${API}/loads/import-batches/${b.id}/file`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = b.file_name || 'import'; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Source file unavailable');
    }
  };

  const undo = async (b) => {
    setUndoing(b.id);
    try {
      const res = await axios.post(`${API}/loads/import-batches/${b.id}/undo`, {}, { withCredentials: true });
      const r = res.data.result || {};
      toast.success(`Undo complete — ${r.deleted?.length || 0} removed, ${r.restored?.length || 0} restored${r.skipped?.length ? `, ${r.skipped.length} kept` : ''}`);
      setItems((prev) => prev.map((x) => (x.id === b.id ? res.data.batch : x)));
      onUndone?.(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Undo failed');
    } finally {
      setUndoing(null);
      setConfirmId(null);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-stretch sm:items-center justify-center p-0 sm:p-4" data-testid="import-history-modal">
      <div className="bg-white w-full max-w-5xl rounded-none sm:rounded-md shadow-2xl flex flex-col max-h-screen sm:max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <History className="w-5 h-5 text-[#002FA7]" />
            <div>
              <h2 className="text-xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Import History</h2>
              <p className="text-xs text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Every import batch — who ran it, the source file, and a one-click undo. Loads already invoiced, on a payroll, or in a locked pay period are kept.</p>
            </div>
          </div>
          <button data-testid="close-import-history" onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          {loading && <div className="p-10 flex items-center justify-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
          {!loading && items.length === 0 && (
            <div className="p-12 text-center text-sm text-slate-500" data-testid="import-history-empty">No imports yet. Batches appear here as soon as you import loads.</div>
          )}
          {!loading && items.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  {['When', 'Source', 'File', 'Run by', 'Result', 'Loads', ''].map((h) => (
                    <th key={h} className="py-2 px-4 text-left text-[10px] font-bold uppercase tracking-widest text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((b, idx) => {
                  const meta = SOURCE_META[b.source] || { label: b.source, icon: FileText };
                  const Icon = meta.icon;
                  const undone = !!b.undone_at;
                  return (
                    <tr key={b.id} data-testid={`import-batch-row-${idx}`} className={`border-t border-slate-100 align-top ${undone ? 'opacity-70' : ''}`}>
                      <td className="py-3 px-4 text-xs text-slate-700 whitespace-nowrap">{fmtWhen(b.created_at)}</td>
                      <td className="py-3 px-4 text-xs">
                        <span className="inline-flex items-center gap-1.5 font-semibold text-slate-800"><Icon className="w-3.5 h-3.5 text-[#002FA7]" />{meta.label}</span>
                      </td>
                      <td className="py-3 px-4 text-xs">
                        {b.has_file ? (
                          <button type="button" onClick={() => download(b)} data-testid={`import-batch-file-${idx}`} className="inline-flex items-center gap-1 text-[#002FA7] hover:underline font-medium">
                            <Download className="w-3.5 h-3.5" />{b.file_name}
                          </button>
                        ) : <span className="text-slate-400 italic">{b.file_name || 'not stored'}</span>}
                      </td>
                      <td className="py-3 px-4 text-xs text-slate-700">{b.run_by_name || '—'}</td>
                      <td className="py-3 px-4 text-xs text-slate-700">
                        <span className="text-emerald-700 font-semibold">{b.counts?.created || 0} created</span>
                        {b.counts?.replaced ? <span className="text-blue-700"> · {b.counts.replaced} replaced</span> : null}
                        {b.counts?.skipped ? <span className="text-slate-500"> · {b.counts.skipped} skipped</span> : null}
                        {undone && (
                          <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded" data-testid={`import-batch-undone-${idx}`}>
                            <Undo2 className="w-3 h-3" /> Undone {fmtWhen(b.undone_at)} by {b.undone_by_name}
                          </div>
                        )}
                        {undone && b.undo_result?.skipped?.length > 0 && (
                          <ul className="mt-1 text-[10px] text-slate-500 list-disc list-inside">
                            {b.undo_result.skipped.map((s, i) => <li key={i}>{s.load_ref}: kept ({s.reason})</li>)}
                          </ul>
                        )}
                      </td>
                      <td className="py-3 px-4 text-[11px] text-slate-600 max-w-[220px]">
                        <span className="line-clamp-2">{(b.load_refs || []).filter(Boolean).join(', ') || '—'}</span>
                      </td>
                      <td className="py-3 px-4 text-right whitespace-nowrap">
                        {!undone && confirmId !== b.id && (
                          <button type="button" onClick={() => setConfirmId(b.id)} data-testid={`import-batch-undo-${idx}`}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-red-200 text-red-700 text-xs font-semibold hover:bg-red-50">
                            <Undo2 className="w-3.5 h-3.5" /> Undo
                          </button>
                        )}
                        {!undone && confirmId === b.id && (
                          <div className="flex items-center gap-1.5 justify-end" data-testid={`import-batch-undo-confirm-${idx}`}>
                            <span className="text-[11px] text-slate-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-amber-600" /> Remove these loads?</span>
                            <button type="button" onClick={() => undo(b)} disabled={undoing === b.id} data-testid={`import-batch-undo-yes-${idx}`}
                                    className="px-2.5 py-1 rounded-md bg-red-600 text-white text-xs font-bold hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-1">
                              {undoing === b.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Yes, undo
                            </button>
                            <button type="button" onClick={() => setConfirmId(null)} className="px-2 py-1 text-xs text-slate-600 hover:text-slate-900">Cancel</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportHistoryModal;
